// Unified LLM Interface Base

import { filterChatModels } from './chat-models-filter.js'
import type {
  ChatArgs,
  Providers,
  ToolImplementations,
  UnifiedChatResponse,
  UnifiedConfig,
  UnifiedMessage,
  UnifiedResponseMessage,
  UnifiedToolDefinition,
  UnifiedUsage,
} from './types.js'

export interface SingleStepResult {
  status: 'complete' | 'needs_tool_call'
  answer?: string
  toolCalls: UnifiedResponseMessage['toolCalls'] | null
  usage?: UnifiedUsage
  raw: unknown
}

export interface ChatWithToolsArgs {
  ctx?: unknown
  provider: string
  apiKey?: string
  model?: string
  messages: UnifiedMessage[]
  tools?: UnifiedToolDefinition[]
  toolImplementations: ToolImplementations
  config?: UnifiedConfig
  maxIterations?: number
}

export interface ChatWithToolsResult {
  message: UnifiedResponseMessage
  messages: UnifiedMessage[]
  iterations: number
  raw: unknown
}

export interface ExecuteArgs {
  ctx?: unknown
  provider: string
  apiKey?: string
  model?: string
  prompt: string
  tools?: UnifiedToolDefinition[]
  toolImplementations: ToolImplementations
  conversationHistory?: UnifiedMessage[]
  config?: UnifiedConfig
}

export interface ExecuteResult {
  answer?: string
  messages: UnifiedMessage[]
  iterations: number
  toolsUsed: string[]
  usage?: UnifiedUsage
}

export interface AgentArgs {
  ctx?: unknown
  provider: string
  apiKey?: string
  model?: string
  systemPrompt?: string
  tools?: UnifiedToolDefinition[]
  toolImplementations: ToolImplementations
  config?: UnifiedConfig
}

export interface Agent {
  chat(prompt: string): Promise<{
    answer?: string
    toolsUsed: string[]
    iterations: number
    usage?: UnifiedUsage
  }>
  getHistory(): UnifiedMessage[]
  clearHistory(): void
}

export interface UnifiedInterface {
  chat(args: { provider: string } & ChatArgs): Promise<UnifiedChatResponse>
  chatSingleStep(args: { provider: string } & ChatArgs): Promise<SingleStepResult>
  chatWithTools(args: ChatWithToolsArgs): Promise<ChatWithToolsResult>
  listModels(args: { provider: string; apiKey?: string }): Promise<unknown>
  listChatModels(args: { provider: string; apiKey?: string }): Promise<string[]>
  execute(args: ExecuteArgs): Promise<ExecuteResult>
  createAgent(args: AgentArgs): Agent
}

export function createUnifiedInterface(providers: Providers): UnifiedInterface {
  const providerNames = Object.keys(providers)

  const chat = async ({
    provider,
    apiKey,
    model,
    messages,
    tools,
    config = {},
    ...options
  }: { provider: string } & ChatArgs): Promise<UnifiedChatResponse> => {
    const providerImpl = providers[provider]
    if (!providerImpl) {
      throw new Error(`Provider ${provider} not supported. Available: ${providerNames.join(', ')}`)
    }

    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    try {
      return await providerImpl.chat({ apiKey, model, messages, tools, config, ...options })
    } catch (error: any) {
      throw new Error(`[${provider}] Chat failed: ${error.message}`, { cause: error })
    }
  }

  const listModels = async ({ provider, apiKey }: { provider: string; apiKey?: string }) => {
    const providerImpl = providers[provider]
    if (!providerImpl) {
      throw new Error(`Provider ${provider} not supported`)
    }
    return await providerImpl.listModels({ apiKey })
  }

  const listChatModels = async ({
    provider,
    apiKey,
  }: { provider: string; apiKey?: string }): Promise<string[]> => {
    const raw = await listModels({ provider, apiKey })
    return filterChatModels(provider, raw)
  }

  const chatSingleStep = async ({
    provider,
    apiKey,
    model,
    messages,
    tools,
    config = {},
  }: { provider: string } & ChatArgs): Promise<SingleStepResult> => {
    const response = await chat({ provider, apiKey, model, messages, tools, config })
    const { message } = response

    if (!message.toolCalls || message.toolCalls.length === 0) {
      return {
        status: 'complete',
        answer: message.content,
        toolCalls: null,
        usage: message.usage,
        raw: response.raw,
      }
    }

    return {
      status: 'needs_tool_call',
      answer: message.content,
      toolCalls: message.toolCalls,
      usage: message.usage,
      raw: response.raw,
    }
  }

  const chatWithTools = async ({
    ctx,
    provider,
    apiKey,
    model,
    messages,
    tools,
    toolImplementations,
    config = {},
    maxIterations = 5,
  }: ChatWithToolsArgs): Promise<ChatWithToolsResult> => {
    const currentMessages: UnifiedMessage[] = [...messages]
    let iteration = 0

    while (iteration < maxIterations) {
      iteration++

      const response = await chat({
        provider,
        apiKey,
        model,
        messages: currentMessages,
        tools,
        config,
      })

      const { message } = response

      if (!message.toolCalls || message.toolCalls.length === 0) {
        return {
          message,
          messages: currentMessages,
          iterations: iteration,
          raw: response.raw,
        }
      }

      currentMessages.push({
        role: 'assistant',
        content: message.content || null,
        toolCalls: message.toolCalls,
      })

      for (const toolCall of message.toolCalls) {
        const toolName = toolCall.function.name
        const toolImpl = toolImplementations[toolName]

        if (!toolImpl) {
          throw new Error(`Tool implementation not found: ${toolName}`)
        }

        const args =
          typeof toolCall.function.arguments === 'string'
            ? JSON.parse(toolCall.function.arguments)
            : toolCall.function.arguments
        const result = await toolImpl(ctx, args)

        currentMessages.push({
          role: 'tool',
          toolCallId: toolCall.id,
          toolName: toolName,
          content: JSON.stringify(result),
        })
      }

      if (provider === 'gemini') {
        currentMessages.push({
          role: 'user',
          content: 'Please provide the answer based on the function results.',
        })
      }
    }

    throw new Error(`Max iterations (${maxIterations}) reached without final answer`)
  }

  const execute = async ({
    ctx,
    provider,
    apiKey,
    model,
    prompt,
    tools,
    toolImplementations,
    conversationHistory = [],
    config = {},
  }: ExecuteArgs): Promise<ExecuteResult> => {
    const messages: UnifiedMessage[] = [...conversationHistory, { role: 'user', content: prompt }]

    const result = await chatWithTools({
      ctx,
      provider,
      apiKey,
      model,
      messages,
      tools,
      toolImplementations,
      config,
      maxIterations: (config.maxIterations as number) || 5,
    })

    const toolsUsed: string[] = []
    for (const msg of result.messages) {
      if (msg.role === 'assistant' && msg.toolCalls) {
        msg.toolCalls.forEach(tc => {
          if (!toolsUsed.includes(tc.function.name)) {
            toolsUsed.push(tc.function.name)
          }
        })
      }
    }

    return {
      answer: result.message.content,
      messages: result.messages,
      iterations: result.iterations,
      toolsUsed,
      usage: result.message.usage,
    }
  }

  const createAgent = ({
    ctx,
    provider,
    apiKey,
    model,
    systemPrompt,
    tools,
    toolImplementations,
    config = {},
  }: AgentArgs): Agent => {
    const conversationHistory: UnifiedMessage[] = []

    if (systemPrompt) {
      conversationHistory.push({ role: 'system', content: systemPrompt })
    }

    return {
      async chat(prompt) {
        const result = await execute({
          ctx,
          provider,
          apiKey,
          model,
          prompt,
          tools,
          toolImplementations,
          conversationHistory,
          config,
        })

        conversationHistory.push(...result.messages.slice(conversationHistory.length))

        return {
          answer: result.answer,
          toolsUsed: result.toolsUsed,
          iterations: result.iterations,
          usage: result.usage,
        }
      },

      getHistory() {
        return [...conversationHistory]
      },

      clearHistory() {
        const systemMessages = conversationHistory.filter(msg => msg.role === 'system')
        conversationHistory.length = 0
        conversationHistory.push(...systemMessages)
      },
    }
  }

  return {
    chat,
    chatSingleStep,
    chatWithTools,
    listModels,
    listChatModels,
    execute,
    createAgent,
  }
}
